import React, { useCallback } from 'react';
import { startOfToday } from 'date-fns';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useCreateBuslineImports } from '@area-restrita/api-client/endpoints/busline-imports/busline-imports.gen';

import FileUploader from '@/components/FileUploader';
import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import CalendarInput from '@/components/CalendarInput';
import { useDialog } from '@/components/template/Dialog';
import { fileWithPreviewSchema } from '@/hooks/useFileUpload';

const schema = z.object({
  files: z
    .array(fileWithPreviewSchema, {
      required_error: 'Arquivo obrigatório',
      invalid_type_error: 'Arquivo inválido',
    })
    .min(1, 'Arquivo obrigatório'),
  date: z
    .date({
      required_error: 'Data obrigatória',
      invalid_type_error: 'Data inválida',
    })
    .min(startOfToday(), 'A data não pode ser no passado'),
});

type FormData = z.infer<typeof schema>;

const ImportDialog: React.FC = () => {
  const { handleClose } = useDialog();
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const { mutateAsync: createBuslineImports } = useCreateBuslineImports();

  const handleSubmit = useCallback(
    async (data: FormData) =>
      createBuslineImports(
        {
          data: {
            file:
              data.files[0].file instanceof File
                ? data.files[0].file
                : await fetch(data.files[0].file.url).then((r) => r.blob()),
            date: data.date.toISOString(),
          },
        },
        {
          onSuccess: () => {
            form.reset();
            handleClose();
            toast.success('Importação criada com sucesso');
          },
          onError: () => {
            toast.error('Erro ao criar importação');
          },
        },
      ),
    [form, handleClose, createBuslineImports],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="grid gap-4 py-4">
          <FormField
            control={form.control}
            name="files"
            render={({ field: { onChange } }) => (
              <FormItem>
                <FormControl>
                  <FileUploader
                    accept="csv, text/csv"
                    maxSize={10 * 1024 * 1024}
                    onFilesChange={onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data da atualização</FormLabel>
                <FormControl>
                  <CalendarInput
                    selected={field.value}
                    onSelect={field.onChange}
                    initialFocus
                    mode="single"
                    disabled={{ before: startOfToday() }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <DialogFooter className="sm:justify-between">
          <a href="/modelo.csv" target="_blank" rel="noopener noreferrer">
            <Button type="button" variant="outline">
              Baixar modelo
            </Button>
          </a>

          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting && (
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
            Salvar
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default ImportDialog;
